import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { PhoneForwarded, Upload, FileDown, RefreshCw, Loader2, ChevronDown, Download, CheckCircle2, Eraser } from "lucide-react";
import { downloadExport } from "@/lib/downloadExport";
import { toast } from "sonner";

/** Ooredoo SOHO — Client Number Refresh: dry-run preview → apply → history + before/after report. */
export default function NumberRefreshPanel() {
  const { activeSlug } = useClient();
  const { hasPerm } = useAuth();
  const canUpload = hasPerm("leads.import");
  const fileRef = useRef(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [reportBatch, setReportBatch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dup, setDup] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);

  const dupPreview = async () => {
    setDupBusy(true);
    try { setDup((await api.get("/leads/number-refresh/duplicate-retired")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Clean-up preview failed"); }
    finally { setDupBusy(false); }
  };
  const dupFix = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.post("/leads/number-refresh/fix-duplicate-retired");
      toast.success(`Removed ${data.entries_removed} duplicated replaced number(s) on ${data.leads_fixed} lead(s)`);
      setDup(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Clean-up failed"); }
    finally { setDupBusy(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leads/number-refresh/batches")
      .then((r) => setBatches(r.data.batches || []))
      .catch(() => toast.error("Failed to load refresh history"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); setPreview(null); setResult(null); }, [load, activeSlug]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/leads/number-refresh/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview({ ...data, file_name: f.name });
      toast.success(`Preview ready — ${data.summary.matched} lead(s) matched, ${data.summary.skipped} row(s) skipped`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setUploading(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/leads/number-refresh/${preview.batch_id}/apply`);
      setResult({ ...data.result, batch_id: data.batch_id });
      setPreview(null);
      toast.success(`${data.result.leads_refreshed} lead(s) refreshed and re-queued${data.result.leads_unchanged ? ` · ${data.result.leads_unchanged} unchanged` : ""}`);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `Apply failed${err?.response?.status ? ` (HTTP ${err.response.status})` : " (no response — network/timeout)"}`, { duration: 12000 });
      load();
    } finally {
      setApplying(false);
    }
  };

  const downloadBlob = async (path, name) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadReport = async (format, batchId) => {
    const params = { format };
    const bid = batchId ?? reportBatch;
    if (bid) params.batch_id = bid;
    else { if (dateFrom) params.date_from = dateFrom; if (dateTo) params.date_to = dateTo; }
    try {
      await downloadExport("/leads/number-refresh/report", params,
        `kippu-number-refresh-report-${activeSlug}-${new Date().toISOString().slice(0, 10)}.${format}`);
      toast.success(`Refresh report downloaded (.${format})`);
    } catch {
      toast.error("Report download failed");
    }
  };

  const s = preview?.summary;

  return (
    <Card data-testid="number-refresh-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <PhoneForwarded className="w-4 h-4 text-slate-500" /> Client Number Refresh
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="refresh-template-btn"
                  onClick={() => downloadBlob("/leads/number-refresh/template", "kippu-number-refresh-template.xlsx")}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-batches-reload">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {canUpload && (
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-refresh-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {uploading ? "Checking…" : "Upload refresh sheet"}
            </Button>
          )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} data-testid="refresh-file-input" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-slate-500 leading-snug">
          Upload the client&apos;s sheet in the same base format as Lead Upload — <strong>CustomerId · Phone1 · Phone2 · Phone3</strong>, <em>Check</em> optional
          (older headers such as <em>Customer ID</em> / <em>New Contact Number 1–3</em> still work; extra columns are ignored).
          Phone1–3 are the customer&apos;s complete current set: numbers Kippu already has stay live, missing ones are marked <em>Replaced by client</em>,
          new ones are added as <em>Client refresh</em>. Rows that repeat a CustomerId are merged; only 8-digit Qatar numbers are accepted.
          Nothing changes until you press <strong>Apply refresh</strong>; leads with an actual change are re-opened (disposition cleared, attempts 0)
          and un-assigned back into the round-robin queue.
        </p>

        {preview && (
          <div className="border border-indigo-200 bg-indigo-50/60 rounded p-3 space-y-2" data-testid="refresh-preview">
            <div className="text-xs font-medium text-indigo-900">Preview · {preview.file_name}</div>
            {preview.header_mismatch && (
              <div className="border border-amber-300 bg-amber-50 rounded p-2 text-[11px] text-amber-900 space-y-1" data-testid="refresh-header-mismatch">
                <div className="font-medium">Headers not recognised — missing {preview.header_mismatch.missing.join(" and ")}.</div>
                <div><span className="text-amber-700">Found:</span> <span className="font-mono">{preview.header_mismatch.found.join(" · ") || "(none)"}</span></div>
                <div><span className="text-amber-700">Expected:</span> <span className="font-mono">{preview.header_mismatch.expected}</span></div>
                <Button variant="outline" size="sm" className="h-6 text-[10px] mt-1" data-testid="refresh-mismatch-template-btn"
                        onClick={() => downloadBlob("/leads/number-refresh/template", "kippu-number-refresh-template.xlsx")}>
                  <Download className="w-3 h-3 mr-1" /> Download template
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2" data-testid="refresh-preview-summary">
              <Badge variant="outline" className="text-[10px]">Rows: {s.rows_total}</Badge>
              <Badge variant="outline" className="text-[10px]" data-testid="preview-distinct">Customer IDs: {s.distinct_customer_ids ?? "—"}</Badge>
              {(s.merged_rows ?? 0) > 0 && <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="preview-merged">Rows merged: {s.merged_rows}</Badge>}
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]" data-testid="preview-matched">Matched: {s.matched}</Badge>
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]" data-testid="preview-to-add">New numbers to add: {s.numbers_to_activate}</Badge>
              <Badge className={`text-[10px] ${s.not_found ? "bg-rose-100 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`} data-testid="preview-not-found">Customer not found: {s.not_found ?? 0}</Badge>
              <Badge className={`text-[10px] ${s.blank_customer_ids ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`} data-testid="preview-blank-ids">Blank / 0 IDs: {s.blank_customer_ids ?? 0}</Badge>
              <Badge className={`text-[10px] ${s.invalid_numbers ? "bg-rose-100 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`} data-testid="preview-invalid">Invalid numbers: {s.invalid_numbers ?? 0}</Badge>
              <Badge className={`text-[10px] ${s.no_valid_number ? "bg-rose-100 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`} data-testid="preview-no-valid">No valid number: {s.no_valid_number ?? 0}</Badge>
              <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="preview-already-active">Numbers kept: {s.numbers_kept ?? s.already_active_numbers ?? 0}</Badge>
              <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="preview-to-retire">To retire: {s.numbers_to_retire ?? 0}</Badge>
              <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="preview-marker">Already updated (marker): {s.already_updated_marker ?? 0}</Badge>
              <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="preview-no-change">No change: {s.no_change ?? 0}</Badge>
              <Badge className={`text-[10px] ${s.over_three ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`} data-testid="preview-over-three">More than 3 numbers: {s.over_three ?? 0}</Badge>
              {(s.shared_with_other_leads ?? 0) > 0 && <Badge variant="outline" className="text-[10px] text-slate-500">Shared with other leads: {s.shared_with_other_leads}</Badge>}
            </div>
            <div className="text-[11px] text-slate-600">
              Will keep <strong>{s.numbers_kept ?? 0}</strong>, retire <strong>{s.numbers_to_retire}</strong> and add <strong>{s.numbers_to_activate}</strong> number(s) across <strong>{s.matched}</strong> lead(s). Sets are re-checked against each lead at apply time.
            </div>
            {preview.preview?.length > 0 && (
              <div className="border border-slate-200 rounded overflow-x-auto bg-white">
                <table className="w-full text-[11px] min-w-[760px]">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                    <tr><th className="text-left px-2 py-1">Customer</th><th className="text-left px-2 py-1">Kept</th><th className="text-left px-2 py-1">To retire</th>
                        <th className="text-left px-2 py-1">New</th><th className="text-left px-2 py-1">Disp. / Owner (cleared)</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.slice(0, 10).map((p) => (
                      <tr key={p.lead_id} className="border-t border-slate-100" data-testid={`preview-row-${p.customer_id}`}>
                        <td className="px-2 py-1"><span className="font-mono text-indigo-700">{p.customer_id}</span> <span className="text-slate-600">{p.customer_name}</span></td>
                        <td className="px-2 py-1 font-mono text-slate-600" data-testid={`preview-kept-${p.customer_id}`}>{(p.kept || []).join(", ") || "—"}</td>
                        <td className="px-2 py-1 font-mono text-slate-400 line-through">{(p.retire ?? p.current_numbers).join(", ") || "—"}</td>
                        <td className="px-2 py-1 font-mono text-emerald-700">
                          {p.new_numbers.map((n) => n.e164).join(", ")}
                          {p.over_three && <Badge className="ml-1 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[9px]">&gt;3</Badge>}
                        </td>
                        <td className="px-2 py-1 text-slate-500">{p.current_disposition || "—"} / {p.current_owner || "unassigned"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {s.matched > 10 && <div className="text-[10px] text-slate-400 px-2 py-1">…and {s.matched - 10} more</div>}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {canUpload && (
                <Button size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" disabled={applying || !s.matched}
                        onClick={apply} data-testid="apply-refresh-btn">
                  {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Apply refresh · {s.matched} lead(s)
                </Button>
              )}
              {s.skipped + (s.invalid_numbers ?? 0) + (s.numbers_kept ?? 0) + (s.no_change ?? 0) > 0 && (
                <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="preview-download-skipped"
                        onClick={() => downloadBlob(`/leads/number-refresh/${preview.batch_id}/errors`, `number-refresh-skipped-${preview.batch_id.slice(0, 8)}.xlsx`)}>
                  <FileDown className="w-3 h-3 mr-1" /> Download skipped rows
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setPreview(null)} data-testid="discard-preview-btn">Discard</Button>
            </div>
          </div>
        )}

        {result && (
          <div className="flex flex-wrap items-center gap-2 border border-emerald-200 bg-emerald-50 rounded p-2" data-testid="refresh-result">
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Leads refreshed: {result.leads_refreshed}</Badge>
            <Badge variant="outline" className="text-[10px] text-slate-600">Numbers kept: {result.numbers_kept ?? 0}</Badge>
            <Badge variant="outline" className="text-[10px] text-slate-600">Numbers retired: {result.numbers_retired}</Badge>
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">Numbers added: {result.numbers_activated}</Badge>
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Leads re-queued: {result.leads_requeued}</Badge>
            <Badge variant="outline" className="text-[10px] text-slate-600" data-testid="result-unchanged">Leads unchanged: {result.leads_unchanged ?? 0}</Badge>
            {(result.leads_differed ?? 0) > 0 && (
              <>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]" data-testid="result-differed">
                  {result.leads_differed} lead(s) differed from the preview (re-checked at apply time)
                </Badge>
                {result.batch_id && (
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" data-testid="result-download-skipped"
                          onClick={() => downloadBlob(`/leads/number-refresh/${result.batch_id}/errors`, `number-refresh-skipped-${result.batch_id.slice(0, 8)}.xlsx`)}>
                    <FileDown className="w-3 h-3 mr-1" /> Updated skipped rows
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {canUpload && (
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2" data-testid="dup-fix-card">
            <span className="text-[11px] text-slate-600 flex-1 min-w-[220px]">
              <strong>Fix duplicated refresh numbers</strong> — finds leads where a replaced number is identical to a live one
              (earlier double-apply) and removes the duplicate replaced entries. Live numbers and history are kept.
            </span>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" disabled={dupBusy} onClick={dupPreview} data-testid="dup-fix-preview-btn">
              {dupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eraser className="w-3 h-3 mr-1" />} Preview clean-up
            </Button>
          </div>
        )}
        {dup && (
          <div className={`border rounded p-2 space-y-2 ${dup.count ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="dup-fix-preview">
            <div className="text-[11px] text-slate-800">
              <strong data-testid="dup-fix-count">{dup.count}</strong> lead(s) carry <strong>{dup.entries}</strong> duplicated replaced number(s).
            </div>
            {(dup.leads || []).slice(0, 5).map((l) => (
              <div key={l.id} className="text-[10px] font-mono text-rose-700" data-testid={`dup-fix-lead-${l.customer_id}`}>
                {l.customer_id} · {l.customer_name} → {l.duplicates.join(", ")}
              </div>
            ))}
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-[10px] bg-rose-600 hover:bg-rose-700" disabled={dupBusy || !dup.count} onClick={dupFix} data-testid="dup-fix-run-btn">
                Remove {dup.entries} duplicate(s)
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setDup(null)} data-testid="dup-fix-dismiss-btn">Dismiss</Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2" data-testid="refresh-report-card">
          <span className="text-[11px] text-slate-600 flex-1 min-w-[200px]"><strong>Before / after report</strong> — old vs new numbers per refreshed lead, for the client.</span>
          <select className="h-7 text-[11px] border border-slate-200 rounded px-1 bg-white" value={reportBatch}
                  onChange={(e) => setReportBatch(e.target.value)} data-testid="report-batch-select">
            <option value="">All batches / date range</option>
            {batches.filter((b) => b.status === "applied").map((b) => (
              <option key={b.id} value={b.id}>{`${(b.applied_at || b.created_at || "").slice(0, 10)} · ${b.file_name}`}</option>
            ))}
          </select>
          {!reportBatch && (
            <>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 text-[11px] w-[130px]" data-testid="report-date-from" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 text-[11px] w-[130px]" data-testid="report-date-to" />
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="download-refresh-report-btn">
                <FileDown className="w-3 h-3 mr-1" /> Download refresh report <ChevronDown className="w-3 h-3 ml-1 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadReport("csv")} data-testid="refresh-report-csv">CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadReport("xlsx")} data-testid="refresh-report-xlsx">Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {batches.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">No number refreshes yet — upload the client&apos;s sheet to preview.</div>
        ) : (
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-1.5">Date</th><th className="text-left px-2 py-1.5">File</th>
                  <th className="text-left px-2 py-1.5">By</th><th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-right px-2 py-1.5">Rows</th><th className="text-right px-2 py-1.5">Applied</th>
                  <th className="text-right px-2 py-1.5">Skipped</th><th className="text-right px-2 py-1.5">Retired</th>
                  <th className="text-right px-2 py-1.5">Activated</th><th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const sm = b.summary || {}; const r = b.result || {};
                  return (
                    <tr key={b.id} className="border-t border-slate-100" data-testid={`refresh-batch-row-${b.id}`}>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">{(b.applied_at || b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-2 py-1.5 text-slate-700">{b.file_name}</td>
                      <td className="px-2 py-1.5 text-slate-500">{b.applied_by_name || b.uploaded_by_name || b.uploaded_by_email}</td>
                      <td className="px-2 py-1.5">
                        {b.status === "applied"
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">applied</Badge>
                          : b.status === "partial"
                            ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]" title="Some leads failed — press Apply again on the preview to retry">partial</Badge>
                            : <Badge variant="outline" className="text-[10px] text-slate-500">preview only</Badge>}
                      </td>
                      <td className="px-2 py-1.5 text-right">{sm.rows_total ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right">{b.status === "applied" ? (r.leads_refreshed ?? sm.matched) : <span className="text-slate-400">{sm.matched} matched</span>}</td>
                      <td className="px-2 py-1.5 text-right">{sm.skipped > 0 ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">{sm.skipped}</Badge> : <span className="text-slate-400">0</span>}</td>
                      <td className="px-2 py-1.5 text-right text-slate-500">{r.numbers_retired ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right text-slate-500">{r.numbers_activated ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {b.has_error_report && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" data-testid={`refresh-batch-errors-${b.id}`}
                                  onClick={() => downloadBlob(`/leads/number-refresh/${b.id}/errors`, `number-refresh-skipped-${b.id.slice(0, 8)}.xlsx`)}>
                            <FileDown className="w-3 h-3 mr-1" /> Skipped
                          </Button>
                        )}
                        {b.status === "applied" && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-indigo-700" data-testid={`refresh-batch-report-${b.id}`}
                                  onClick={() => downloadReport("xlsx", b.id)}>
                            <FileDown className="w-3 h-3 mr-1" /> Report
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
